import React, { useState } from 'react';
import './LifeStyleImpact.css';

const LifestyleImpact = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const lifestyleFactors = [
    {
      title: 'SLEEP',
      impactLevel: 'HIGH',
      affects: 'DNA fragmentation, count, motility',
      keyFinding: 'Men sleeping <6 hours show 29% lower sperm count compared to those sleeping 7–9 hours.',
      recommendation: 'Optimal Range: 7–9 hours/night',
      studyReference:
        'Wise LA, et al. "Sleep duration and semen quality among men enrolled in a fertility study." Fertility and Sterility. 2018;109(3):453-462.',
    },
    {
      title: 'ALCOHOL',
      impactLevel: 'HIGH',
      affects: 'Motility, morphology, testosterone',
      keyFinding: 'Consumption of >5 drinks per week associated with 33% reduction in sperm motility and altered morphology.',
      recommendation: 'Threshold: Limit to <5 drinks/week',
      studyReference:
        'Jensen TK, et al. "Habitual alcohol consumption associated with reduced semen quality and changes in reproductive hormones." BMJ Open. 2014;4(9):e005462.',
      additionalInfo: 'Timeline: Effects visible 74+ days after reduction',
    },
    {
      title: 'HEAT EXPOSURE',
      impactLevel: 'HIGH',
      affects: 'Count, concentration (temporary)',
      keyFinding: 'Scrotal temperature elevation by just 1°C significantly reduces sperm production. Recovery takes 3–6 months.',
      recommendation: 'Limit sauna/hot tub to <15min, max 2x/week',
      studyReference:
        'Garolla A, et al. "Seminal and molecular evidence that sauna exposure affects human spermatogenesis." Human Reproduction. 2013;28(4):877-885.',
      additionalInfo: 'Sources: Hot tubs, saunas >15min, laptop on lap, tight underwear',
    },
    {
      title: 'SMOKING & VAPING',
      impactLevel: 'HIGH',
      affects: 'All parameters significantly',
      keyFinding: 'Smoking reduces sperm count by 23%, motility by 13%, and morphology by 13%. Effects are dose-dependent.',
      recommendation: 'Quit smoking and vaping for optimal fertility',
      studyReference:
        'Sharma R, et al. "Cigarette smoking and semen quality: a new meta-analysis examining the effect of the 2010 World Health Organization laboratory methods." European Urology. 2016;70(4):635-645.',
      additionalInfo: 'Recovery: Improvement visible 3–6 months after quitting',
    },
    {
      title: 'EXERCISE',
      impactLevel: 'MODERATE',
      affects: 'Oxidative stress, all parameters',
      keyFinding: 'Moderate exercise (4–7 hrs/week) improves sperm quality by 15–20%. Excessive training (>10 hrs/week) may decrease parameters.',
      recommendation: 'Sweet Spot: 4–7 hours moderate exercise per week',
      studyReference:
        'Gaskins AJ, et al. "Physical activity and television watching in relation to semen quality in young men." British Journal of Sports Medicine. 2015;49(4):265-270.',
      additionalInfo: 'Note: Balance is key – both sedentary lifestyle and overtraining can impair fertility',
    },
    {
      title: 'BODY COMPOSITION',
      impactLevel: 'MODERATE',
      affects: 'Hormone balance, concentration',
      keyFinding: 'Obesity (BMI >30) associated with 20% reduction in sperm concentration and testosterone levels.',
      recommendation: 'Optimal BMI: 20–25',
      studyReference:
        'Sermondade N, et al. "BMI in relation to sperm count: an updated systematic review and collaborative meta-analysis." Human Reproduction Update. 2013;19(3):221-231.',
    },
    {
      title: 'STRESS & CORTISOL',
      impactLevel: 'MODERATE',
      affects: 'DNA fragmentation, hormone balance',
      keyFinding: 'High perceived stress and elevated cortisol linked to lower sperm concentration and reduced motility.',
      recommendation: 'Management: Regular meditation, therapy, adequate sleep, exercise',
      studyReference:
        'Nordkap L, et al. "Psychological stress and testicular function: a cross-sectional study of 1,215 Danish men." Fertility and Sterility. 2016;105(1):174-187.',
    },
    {
      title: 'DIET & NUTRITION',
      impactLevel: 'MODERATE',
      affects: 'DNA integrity, all parameters',
      keyFinding: 'Mediterranean diet pattern associated with 40% higher progressive motility and improved overall semen quality.',
      recommendation: 'Protective: Omega-3s, antioxidants (vitamins C, E), zinc, folate, nuts, fish',
      studyReference:
        'Karayiannis D, et al. "Association between adherence to the Mediterranean diet and semen quality parameters in male partners of couples attempting fertility." Human Reproduction. 2017;32(1):215-222.',
    },
    {
      title: 'ENVIRONMENTAL TOXINS',
      impactLevel: 'MODERATE',
      affects: 'Endocrine disruption, DNA damage',
      keyFinding: 'Exposure to BPA, phthalates, and pesticides associated with reduced sperm quality and increased DNA fragmentation.',
      recommendation: 'Minimize: Use glass/stainless steel containers, choose organic when possible',
      studyReference:
        'Jurewicz J, et al. "Human urinary phthalate metabolites level and main semen parameters, sperm chromatin structure, sperm aneuploidy and reproductive hormones." Reproductive Toxicology. 2013;42:232-241.',
    },
    {
      title: 'EVIDENCE-BASED SUPPLEMENTS',
      impactLevel: 'MODERATE',
      affects: 'Motility, DNA integrity, count',
      keyFinding: 'CoQ10, Vitamin D, Zinc, and L-Carnitine improve motility, count, and testosterone. Effects visible after 74+ days.',
      recommendation: 'Timeline: Allow 74+ days to see effects (full sperm development cycle)',
      studyReference: 'Smits RM, et al. "Antioxidants for male subfertility." Cochrane Database of Systematic Reviews. 2019;3(3):CD007411.',
    },
  ];

  const sortedFactors = [...lifestyleFactors].sort((a, b) => {
    if (a.impactLevel === 'HIGH' && b.impactLevel === 'MODERATE') return -1;
    if (a.impactLevel === 'MODERATE' && b.impactLevel === 'HIGH') return 1;
    return 0;
  });

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="lifestyle-impact-container">
      <p className="lifestyle-note">
        Each factor is backed by peer-reviewed research. Changes typically take 74+ days to reflect in your results (one full sperm development
        cycle).
      </p>

      <div className="lifestyle-accordion">
        {sortedFactors.map((factor, index) => (
          <div key={index} className="lifestyle-card">
            <div className="lifestyle-header" onClick={() => toggleAccordion(index)}>
              <div className="lifestyle-header-left">
                <h3>{factor.title}</h3>
              </div>
              <div>
                <span className={`impact-badge ${factor.impactLevel === 'HIGH' ? 'high' : 'moderate'}`}>{factor.impactLevel}</span>
                <span className={`chevron ${openIndex === index ? 'open' : ''}`}>⌄ </span>
              </div>
            </div>

            {openIndex === index && (
              <div className="lifestyle-content">
                <p>
                  <strong>Affects:</strong> {factor.affects}
                </p>
                <p>
                  <strong>Key Finding:</strong> {factor.keyFinding}
                </p>
                <p>
                  <strong>Recommendation:</strong> {factor.recommendation}
                </p>
                {factor.additionalInfo && (
                  <p>
                    <strong>Additional Info:</strong> {factor.additionalInfo}
                  </p>
                )}
                <p className="reference">
                  <strong>Study Reference:</strong> {factor.studyReference}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LifestyleImpact;
